import React, { useState, useMemo, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';

// ─── Report definitions grouped by section ───────────────────────────────────
const SECTIONS = [
  {
    key: 'daily-ops',
    label: 'Daily Operations',
    subtitle: 'Real-time view of today\'s schedule and immediate action items',
    reports: [
      {
        id: 'daily-class-schedule',
        title: 'Daily Class Schedule',
        category: 'Schedule',
        priority: 'normal',
        description:
          'All classes scheduled for today — lecturer, classroom, batch, subject, and time slot. Full operational picture for the day.',
        source: 'class_schedules · batches · staff · classrooms · subjects',
      },
      {
        id: 'partial-attendance',
        title: 'Partial Attendance Report',
        category: 'Attendance',
        priority: 'high',
        description:
          'Students who attended at least one class but missed another on the selected date. Filter by course and subject — subject view splits all students into Present and Absent lists.',
        source: 'class_schedules · attendance · registrations · students',
      },
      {
        id: 'non-active-present',
        title: 'Present Students – Registration Not Active',
        category: 'Attendance',
        priority: 'high',
        description:
          'Students marked present in a class whose registration is not active (inactivated, suspended, pending…). Lists student, batch, course, date, subject, time, lecturer and hall for a selected date range, across all courses.',
        source: 'attendance · registrations · class_schedules · students',
      },
    ],
  },
  {
    key: 'academic-analysis',
    label: 'Academic Analysis',
    subtitle: 'Course-level visibility into ongoing batches, delivery progress, and academic health',
    reports: [
      {
        id: 'course-wise-batches',
        title: 'Course-wise Ongoing Batches',
        category: 'Academic',
        priority: 'normal',
        description:
          'All currently ongoing batches grouped by course — batch name, start and end dates, schedule type, and coordinator. Provides a full picture of active academic delivery across the institution.',
        source: 'batches · courses · staff',
      },
      {
        id: 'batch-subject-attendance',
        title: 'Batch-wise Subject Attendance',
        category: 'Attendance',
        priority: 'normal',
        description:
          'Subject-level attendance percentages for every ongoing batch. Shows both ongoing and completed subjects with classes held, total present, and the overall attendance rate per subject.',
        source: 'batch_subjects · class_schedules · attendance · registrations',
      },
      {
        id: 'lecturer-subject-attendance',
        title: 'Lecturer-wise Subject Attendance',
        category: 'Attendance',
        priority: 'normal',
        description:
          'Subject-level attendance percentages grouped by lecturer. Displays each staff member\'s ongoing and completed subject assignments with attendance rates and overall weighted average.',
        source: 'batch_subjects · staff · class_schedules · attendance · registrations',
      },
      {
        id: 'student-attendance-report',
        title: 'Student Attendance Report',
        category: 'Attendance',
        priority: 'normal',
        description:
          'Each student\'s overall attendance percentage, aggregated across every subject in their batch. Filter to all ongoing batches or a multi-select of specific batches, and optionally show only students at or above a chosen minimum percentage.',
        source: 'registrations · batch_subjects · class_schedules · attendance · students',
      },
      {
        id: 'batch-student-status',
        title: 'Active Batches & Student Status',
        category: 'Academic',
        priority: 'normal',
        description:
          'Every currently ongoing batch grouped by course, listing each enrolled student\'s registration status (active, postponed, suspended, etc.) and delivery status (ongoing, completed, dropped, transferred). Printable as a formal per-course report.',
        source: 'batches · registrations · students · courses',
      },
      {
        id: 'duplicate-course-registrations',
        title: 'Students by Course (Duplicate Check)',
        category: 'Registration',
        priority: 'high',
        description:
          'All students grouped by course name — courses that share a name but have different course IDs are merged into one group, flagging any student registered in two or more batches under what is really the same course.',
        source: 'courses · batches · registrations · students',
      },
      {
        id: 'university-id-status',
        title: 'University ID Status',
        category: 'Academic',
        priority: 'normal',
        description:
          'Batches grouped by course, showing how many enrolled students have been assigned a University ID versus still missing one. Filter by batch stage, view as a course-wise summary table or a per-student detailed list, and print a formal per-course report.',
        source: 'batches · registrations · students · courses',
      },
      {
        id: 'student-portal-access',
        title: 'Student Portal Access Status',
        category: 'Academic',
        priority: 'normal',
        description:
          'Batches grouped by course, showing per student whether their login (username/password) has been shared and whether their parent portal login has been set up — green check if shared, red cross if not. Filter by batch stage and search by student/batch/course.',
        source: 'batches · registrations · students · courses',
      },
      {
        id: 'product-submission',
        title: 'Higher End Product Submission Report',
        category: 'Academic',
        priority: 'normal',
        description:
          'Course-wise, batch-wise submission mark sheet — subjects as columns, one row per student with University ID, University ID updated date, and name. HND courses show D/M/P/R grades; every other course shows a numeric score. English/IT certificate courses are excluded.',
        source: 'batch_subjects · course_subjects · subjects · assignment_submission · topup_assignment_submission · registrations',
      },
      {
        id: 'diploma-completion',
        title: 'Diploma Completion Report',
        category: 'Academic',
        priority: 'normal',
        description:
          'Course-wise, batch-wise diploma phase completion sheet — phases as columns, one row per student with University ID, attendance, and name. Marks and attempt (1st/2nd/3rd) are sourced from the same data used on the Diploma Batch Phases page; read-only here.',
        source: 'custom_diploma_courses · custom_diploma_phases · custom_diploma_batch_phases · registrations',
      },
      {
        id: 'tickets-report',
        title: 'Tickets Report',
        category: 'Support',
        priority: 'normal',
        description:
          'Filterable ticket listing — by course, category, created date range, and student name/ID/registration number search. Plain summary table with an Excel download.',
        source: 'tickets · ticket_categories · staff',
      },
      {
        id: 'ticket-analysis',
        title: 'Ticket Analysis',
        category: 'Support',
        priority: 'normal',
        description:
          'Analytics dashboard for the ticket system — avg. time-to-resolve/close, SLA compliance, course-wise status, category and priority breakdowns, daily volume trend, open-ticket aging, and a 30-day staff resolver leaderboard. Filterable by course and date range.',
        source: 'tickets · ticket_categories · staff · courses',
      },
      {
        id: 'hnd-tracking-history',
        title: 'HND Tracking Sheet Upload History',
        category: 'Academic',
        priority: 'normal',
        description:
          'Course-wise log of every tracking sheet upload — who uploaded, which batch, how many records were updated, and when. Shows the latest update date at a glance with a full chronological history from newest to oldest.',
        source: 'hnd_tracking_upload_history · batches · courses · staff',
      },
      {
        id: 'hnd-submission-analysis',
        title: 'HND Repeat & Not Submission Analysis',
        category: 'Academic',
        priority: 'high',
        description:
          'Batch-wise breakdown of HND students with a Repeat mark (R) or no submission recorded, for every subject whose actual assignment submission deadline has already passed. Filter by course, batch (multi-select), and batch delivery status (multi-select).',
        source: 'batch_subjects · assignment_submission · registrations · courses · subjects',
      },
    ],
  },
  {
    key: 'assignment',
    label: 'Assignment Corrections',
    subtitle: 'Submitted assignments awaiting marks, flagged by how long they have been pending',
    reports: [
      {
        id: 'not-corrected-overdue',
        title: '14 Days Passed Not Corrected Assignment',
        category: 'Assignment',
        priority: 'high',
        description:
          'Every submitted assignment still without marks, grouped by the lecturer allocated to each batch subject. Flags submissions pending 14+ days since submission as overdue, separately from those still within the normal window.',
        source: 'assignment_submission · topup_assignment_submission · batch_subjects · staff',
      },
    ],
  },
  {
    key: 'infrastructure',
    label: 'Infrastructure',
    subtitle: 'Physical resource allocation and capacity planning',
    reports: [
      {
        id: 'classroom-utilization',
        title: 'Classroom Utilization Report',
        category: 'Infrastructure',
        priority: 'normal',
        description:
          'Room-wise usage for a chosen date range, split into weekday and weekend sessions. Shows booked hours, blocked hours, and utilization % against assumed operating hours — a resource-allocation view suitable for capacity-planning and management reporting.',
        source: 'class_schedules · classrooms · classroom_blocks',
      },
    ],
  },
  {
    key: 'staff-analysis',
    label: 'Staff Analysis',
    subtitle: 'Teaching load, subject delivery progress, and attendance performance per staff member',
    reports: [
      {
        id: 'staff-analysis',
        title: 'Staff Analysis Report',
        category: 'Staff',
        priority: 'normal',
        description:
          'Executive summary of all lecturers — teaching load, ongoing and completed subjects, total students under instruction, and overall attendance percentage. Ranked by attendance performance.',
        source: 'batch_subjects · staff · class_schedules · attendance · registrations',
      },
      {
        id: 'lecturer-ratings',
        title: 'Favorite Lecturer Evaluation',
        category: 'Staff',
        priority: 'normal',
        description:
          'Lecturers ranked by average student rating (1–5 stars) for a chosen date range, highest score first. View Detail opens each lecturer\'s individual ratings and comments in a new tab.',
        source: 'lecturer_ratings · staff · students · subjects · batches',
      },
    ],
  },
];

// ─── Category visual config ────────────────────────────────────────────────────
const CATEGORY_META = {
  Alert:            { bg: 'bg-red-50',     icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',                                                                                                           text: 'text-red-600',    badge: 'bg-red-100 text-red-700' },
  Schedule:         { bg: 'bg-blue-50',    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z',                                                                                                                                               text: 'text-blue-600',   badge: 'bg-blue-100 text-blue-700' },
  Attendance:       { bg: 'bg-green-50',   icon: 'M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',                                                                                                                                                                                             text: 'text-green-600',  badge: 'bg-green-100 text-green-700' },
  Assignment:       { bg: 'bg-violet-50',  icon: 'M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 0 1 0 2.828L11.828 15H9v-2.828l8.586-8.586a2 2 0 0 1 2.828 0z',                                                                                               text: 'text-violet-600', badge: 'bg-violet-100 text-violet-700' },
  Academic:         { bg: 'bg-teal-50',    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', text: 'text-teal-600',   badge: 'bg-teal-100 text-teal-700' },
  'Student Affairs':{ bg: 'bg-rose-50',    icon: 'M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',                        text: 'text-rose-600',   badge: 'bg-rose-100 text-rose-700' },
  Staff:            { bg: 'bg-purple-50',  icon: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z',                                                                                                                                                                   text: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
  Infrastructure:   { bg: 'bg-orange-50',  icon: 'M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4',                                                                                        text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' },
  Administration:   { bg: 'bg-yellow-50',  icon: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4', text: 'text-yellow-600', badge: 'bg-yellow-100 text-yellow-700' },
  Support:          { bg: 'bg-cyan-50',    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 0 1-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',                                                                            text: 'text-cyan-600',   badge: 'bg-cyan-100 text-cyan-700' },
  Registration:     { bg: 'bg-rose-50',    icon: 'M8 7V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M8 7H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-2M8 7h8v8H8V7z',                                                                                                                text: 'text-rose-600',   badge: 'bg-rose-100 text-rose-700' },
};

function CategoryIcon({ category }) {
  const m = CATEGORY_META[category] || CATEGORY_META.Administration;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={`w-5 h-5 ${m.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d={m.icon} />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
    </svg>
  );
}

// Map report IDs to their view URL builders. Returns null when not yet implemented.
const REPORT_URLS = {
  'daily-class-schedule': () => {
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return `/admin/report/daily-class-schedule?date=${ymd}`;
  },
  'partial-attendance': () => '/admin/report/partial-attendance',
  'non-active-present': () => '/admin/report/non-active-present',
  'course-wise-batches':         () => '/admin/report/course-wise-batches',
  'batch-subject-attendance':     () => '/admin/report/batch-subject-attendance',
  'lecturer-subject-attendance':  () => '/admin/report/lecturer-subject-attendance',
  'student-attendance-report':   () => '/admin/report/student-attendance',
  'batch-student-status':        () => '/admin/report/batch-student-status',
  'duplicate-course-registrations': () => '/admin/report/duplicate-course-registrations',
  'university-id-status':        () => '/admin/report/university-id-status',
  'student-portal-access':       () => '/admin/report/student-portal-access',
  'product-submission':          () => '/admin/report/product-submission',
  'diploma-completion':          () => '/admin/report/diploma-completion',
  'tickets-report':              () => '/admin/report/tickets',
  'ticket-analysis':              () => '/admin/report/ticket-analysis',
  'classroom-utilization':       () => '/admin/report/classroom-utilization',
  'staff-analysis':               () => '/admin/report/staff-analysis',
  'lecturer-ratings':              () => '/admin/report/lecturer-ratings',
  'not-corrected-overdue':        () => '/admin/report/not-corrected-overdue',
  'hnd-tracking-history':         () => '/admin/report/hnd-tracking-history',
  'hnd-submission-analysis':      () => '/admin/report/hnd-submission-analysis',
};

export default function DailyMonitorReport() {
  const [searchQuery, setSearchQuery] = useState('');

  const handleViewReport = useCallback((reportId) => {
    const urlBuilder = REPORT_URLS[reportId];
    if (urlBuilder) {
      window.open(urlBuilder(), '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Flatten all reports and filter, preserving section structure
  const filteredSections = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((sec) => ({
      ...sec,
      reports: sec.reports.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.category.toLowerCase().includes(q) ||
          sec.label.toLowerCase().includes(q)
      ),
    })).filter((sec) => sec.reports.length > 0);
  }, [searchQuery]);

  const totalVisible = filteredSections.reduce((n, s) => n + s.reports.length, 0);

  return (
    <DashboardLayout title="Monitor Report">
      <div className="min-h-screen bg-gray-50 px-6 py-8">

        {/* Page Header */}
        <div className="mb-8 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Monitor Report</h1>
          </div>
          <p className="text-sm text-gray-500 ml-11">
            Comprehensive quality monitoring across attendance, assignments, delivery, and student welfare.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="Search by report title, category, or description…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {searchQuery && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {totalVisible} result{totalVisible !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* No results */}
        {totalVisible === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
            </svg>
            <p className="text-sm">No reports match your search.</p>
          </div>
        )}

        {/* Sections */}
        <div className="space-y-10">
          {filteredSections.map((section) => (
            <div key={section.key}>
              {/* Section header */}
              <div className="mb-4">
                <h2 className="text-sm font-bold text-gray-800">{section.label}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{section.subtitle}</p>
              </div>

              {/* Cards grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.reports.map((report) => {
                  const meta = CATEGORY_META[report.category] || CATEGORY_META.Administration;
                  const isHighPriority = report.priority === 'high';
                  const hasUrl = Boolean(REPORT_URLS[report.id]);
                  return (
                    <div
                      key={report.id}
                      className={`bg-white rounded-xl border flex flex-col transition-shadow duration-200 hover:shadow-md ${isHighPriority ? 'border-red-100' : 'border-gray-200'} shadow-sm`}
                    >
                      {/* High priority stripe */}
                      {isHighPriority && <div className="h-0.5 w-full bg-red-400 rounded-t-xl" />}

                      {/* Body */}
                      <div className="p-5 flex-1">
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-9 h-9 ${meta.bg} rounded-lg flex items-center justify-center mt-0.5`}>
                            <CategoryIcon category={report.category} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.badge}`}>
                                {report.category}
                              </span>
                              {isHighPriority && (
                                <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-500">
                                  Action Required
                                </span>
                              )}
                            </div>
                            <h3 className="text-sm font-semibold text-gray-900 leading-snug mt-1.5">{report.title}</h3>
                          </div>
                        </div>
                        <p className="mt-3 text-xs text-gray-500 leading-relaxed">{report.description}</p>
                      </div>

                      {/* Footer */}
                      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between rounded-b-xl">
                        <span className="text-[10px] text-gray-300 truncate max-w-[55%]" title={report.source}>
                          {report.source}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleViewReport(report.id)}
                          disabled={!hasUrl}
                          title={hasUrl ? 'Open report in new tab' : 'Report coming soon'}
                          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${hasUrl ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7S1.274 16.057 0 12z" />
                          </svg>
                          {hasUrl ? 'View Report' : 'Coming Soon'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
